
import { Brain, Heart, Target, BookOpen, Users, Lightbulb, Shield, Zap, Activity, Dna, Clock, TrendingUp } from 'lucide-react';
import { AnalysisSection } from './types';
import { extractContent } from './textProcessing';

// Parse string-based analysis
export const parseStringAnalysis = (analysis: string): AnalysisSection[] => {
  const cleanedAnalysis = analysis
    .replace(/```json\s*\{?/g, '')
    .replace(/```\s*\}?$/g, '')
    .replace(/\\n/g, '\n')
    .replace(/\\"/g, '"')
    .replace(/\\'/g, "'")
    .replace(/\\\\/g, '\\')
    .trim();
  
  // Try to extract sections from markdown-style headers
  const sections = cleanedAnalysis.split(/(?=##?\s+[A-ZÁÇÕ])/g).filter(section => section.trim());
  
  if (sections.length === 0) {
    // If no sections found, create a single section with all content
    return [{
      title: 'Análise Completa',
      content: cleanedAnalysis,
      icon: Brain,
      category: 'overview'
    }];
  }
  
  return sections.map((section, index) => {
    const lines = section.trim().split('\n');
    const title = lines[0].replace(/^##?\s*/, '').trim();
    const content = lines.slice(1).join('\n').trim();
    
    return { 
      title, 
      content: content || 'Análise detalhada não disponível nesta seção.',
      icon: getIconForSection(title),
      category: getCategoryForSection(title, index)
    };
  });
};

// Parse object-based analysis with comprehensive field mapping
export const parseObjectAnalysis = (analysis: any): AnalysisSection[] => {
  const sectionDefinitions = [
    {
      title: 'Resumo Executivo',
      keys: ['executive_summary', 'summary', 'resumo_executivo', 'overview'],
      icon: Brain,
      category: 'overview'
    },
    {
      title: 'Abertura à Experiência - Análise Profunda',
      keys: ['openness_deep_dive', 'openness_analysis', 'abertura_experiencia', 'detailed_trait_analysis.openness'],
      icon: Lightbulb,
      category: 'traits'
    },
    {
      title: 'Conscienciosidade - Análise Profunda',
      keys: ['conscientiousness_deep_dive', 'conscientiousness_analysis', 'conscienciosidade'],
      icon: Target,
      category: 'traits'
    },
    {
      title: 'Extroversão - Análise Profunda',
      keys: ['extraversion_deep_dive', 'extraversion_analysis', 'extroversao'],
      icon: Users,
      category: 'traits'
    },
    {
      title: 'Amabilidade - Análise Profunda',
      keys: ['agreeableness_deep_dive', 'agreeableness_analysis', 'amabilidade'],
      icon: Heart,
      category: 'traits'
    },
    {
      title: 'Neuroticismo - Análise Profunda',
      keys: ['neuroticism_deep_dive', 'neuroticism_analysis', 'neuroticismo'],
      icon: Activity,
      category: 'traits'
    },
    {
      title: 'Arquitetura Psicológica',
      keys: ['psychological_architecture', 'arquitetura_psicologica', 'psychological_profile'],
      icon: Brain,
      category: 'psychology'
    },
    {
      title: 'Dinâmicas Interpessoais',
      keys: ['interpersonal_dynamics_analysis', 'dinamicas_interpessoais', 'interpersonal_dynamics'],
      icon: Users,
      category: 'psychology'
    },
    {
      title: 'Manifestações Comportamentais',
      keys: ['behavioral_manifestations', 'manifestacoes_comportamentais', 'behavioral_patterns'],
      icon: Activity,
      category: 'psychology'
    },
    {
      title: 'Estilo de Processamento Cognitivo',
      keys: ['cognitive_processing_style', 'estilo_cognitivo', 'cognitive_style'],
      icon: Brain,
      category: 'psychology'
    },
    {
      title: 'Sistema de Regulação Emocional',
      keys: ['emotional_regulation_system', 'regulacao_emocional', 'emotional_regulation'],
      icon: Heart,
      category: 'psychology'
    },
    {
      title: 'Mecanismos de Defesa',
      keys: ['defense_mechanisms_analysis', 'mecanismos_defesa', 'defense_mechanisms'],
      icon: Shield,
      category: 'psychology'
    },
    {
      title: 'Vulnerabilidades Psicológicas',
      keys: ['psychological_vulnerabilities', 'vulnerabilidades_psicologicas', 'vulnerabilities'],
      icon: Zap,
      category: 'clinical'
    },
    {
      title: 'Forças e Recursos Psicológicos',
      keys: ['psychological_strengths_resources', 'forcas_recursos', 'strengths_resources', 'strengths'],
      icon: TrendingUp,
      category: 'clinical'
    },
    {
      title: 'Correlatos Neurobiológicos',
      keys: ['neurobiological_correlates', 'correlatos_neurobiologicos', 'neurobiological'],
      icon: Dna,
      category: 'clinical'
    },
    {
      title: 'Perspectiva Desenvolvimental',
      keys: ['developmental_perspective', 'perspectiva_desenvolvimental', 'developmental'],
      icon: Clock,
      category: 'clinical'
    },
    {
      title: 'Recomendações Terapêuticas Detalhadas',
      keys: ['therapeutic_recommendations_detailed', 'therapeutic_recommendations', 'recomendacoes_terapeuticas'],
      icon: Lightbulb,
      category: 'recommendations'
    },
    {
      title: 'Insights Psicanalíticos Expandidos',
      keys: ['psychoanalytic_insights_expanded', 'psychoanalytic_insights', 'insights_psicanaliticos'],
      icon: BookOpen,
      category: 'recommendations'
    },
    {
      title: 'Síntese Integrativa Abrangente',
      keys: ['integration_synthesis_comprehensive', 'integration_synthesis', 'sintese_integrativa'],
      icon: Target,
      category: 'recommendations'
    },
    {
      title: 'Insights Práticos e Acionáveis',
      keys: ['actionable_insights_practical', 'actionable_insights', 'insights_praticos'],
      icon: Lightbulb,
      category: 'recommendations'
    }
  ];

  const sections: AnalysisSection[] = [];

  for (const definition of sectionDefinitions) {
    let content = '';
    
    // Try each key until we find content
    for (const key of definition.keys) {
      content = extractContent(analysis, key, '');
      if (content && content !== 'Conteúdo não disponível' && content.length > 50) {
        break;
      }
    }
    
    // Only add sections with meaningful content
    if (content && content !== 'Conteúdo não disponível' && content.length > 50) {
      sections.push({
        title: definition.title,
        content,
        icon: definition.icon,
        category: definition.category
      });
    }
  }

  console.log(`✅ Análise concluída. ${sections.length} seções com conteúdo encontradas.`);
  return sections;
};

// Helper functions
const getIconForSection = (title: string) => {
  const titleLower = title.toLowerCase();
  if (titleLower.includes('abertura') || titleLower.includes('openness')) return Lightbulb;
  if (titleLower.includes('conscienciosidade') || titleLower.includes('conscientiousness')) return Target;
  if (titleLower.includes('extroversão') || titleLower.includes('extraversion')) return Users;
  if (titleLower.includes('amabilidade') || titleLower.includes('agreeableness')) return Heart;
  if (titleLower.includes('neuroticismo') || titleLower.includes('neuroticism')) return Activity;
  if (titleLower.includes('defesa') || titleLower.includes('defense')) return Shield;
  if (titleLower.includes('vulnerabilidades') || titleLower.includes('vulnerabilities')) return Zap;
  if (titleLower.includes('forças') || titleLower.includes('strengths')) return TrendingUp;
  if (titleLower.includes('neurobiológicos') || titleLower.includes('neurobiological')) return Dna;
  if (titleLower.includes('desenvolvimental') || titleLower.includes('developmental')) return Clock;
  if (titleLower.includes('recomendações') || titleLower.includes('recommendations')) return Lightbulb;
  if (titleLower.includes('insights') || titleLower.includes('psicanalíticos')) return BookOpen;
  return Brain;
};

const getCategoryForSection = (title: string, index: number): string => {
  const titleLower = title.toLowerCase();
  if (titleLower.includes('resumo') || titleLower.includes('overview') || index === 0) return 'overview';
  if (titleLower.includes('abertura') || titleLower.includes('conscienciosidade') || 
      titleLower.includes('extroversão') || titleLower.includes('amabilidade') || 
      titleLower.includes('neuroticismo') || titleLower.includes('traço')) return 'traits';
  if (titleLower.includes('vulnerabilidades') || titleLower.includes('forças') || 
      titleLower.includes('neurobiológicos') || titleLower.includes('desenvolvimental')) return 'clinical';
  if (titleLower.includes('recomendações') || titleLower.includes('insights') || 
      titleLower.includes('síntese') || titleLower.includes('acionáveis')) return 'recommendations';
  return 'psychology';
};
